import { useEffect, useRef, useState } from "react";
import { readPopupMessage, watchPopupClosed } from "@/lib/popup-sign-in";

interface PopupHandlers {
  /** The popup reported back: `null` on success, else Better Auth's error code. */
  onReport: (error: string | null) => void;
  /** The popup closed without a report (closed by the person, or the opener was severed). */
  onClosed: () => void;
}

/**
 * A Microsoft popup opened from inside Helm's frame (`src/lib/popup-complete.ts` serves its
 * pages). One attempt at a time; the popup's report and the close-watch can both end it, so the
 * first wins. Handlers always see the latest render's closures.
 */
export function usePopup(handlers: PopupHandlers) {
  const [waiting, setWaiting] = useState(false);
  const attempt = useRef<{ settled: boolean; cancelWatch: () => void } | null>(null);
  const mounted = useRef(true);
  const latest = useRef(handlers);
  latest.current = handlers;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      attempt.current?.cancelWatch();
    };
  }, []);

  /** Ends the current attempt once; true when this call is the one that ended it. */
  function end(): boolean {
    const current = attempt.current;
    if (!current || current.settled) return false;
    current.settled = true;
    current.cancelWatch();
    if (mounted.current) setWaiting(false);
    return true;
  }

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const report = readPopupMessage(event.origin, event.data, window.location.origin);
      if (report && end()) latest.current.onReport(report.error);
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  });

  /** Opens `path` in a popup; false when the browser blocked it. */
  function open(path: string, name: string): boolean {
    const popup = window.open(path, name, "width=520,height=640");
    if (!popup) return false;
    attempt.current?.cancelWatch();
    setWaiting(true);
    const cancelWatch = watchPopupClosed(popup, () => {
      if (end()) latest.current.onClosed();
    });
    attempt.current = { settled: false, cancelWatch };
    return true;
  }

  return { open, waiting };
}
